//* react
import React from 'react';

//* material-ui
import { Box, Button, Fade, List, ListItemButton, Stack, type SxProps, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';

//* types
import { TemplatesMenuFilters, TemplatesMenuProps } from 'types/templates/templates-menu';

//* system
import { useSelector } from 'store';
import useLocalization from 'hooks/useLocalization';
import TemplatesMenuHeader from './TemplatesMenuHeader';
import MenuSingleTemplate from './single-template';
import TemplateInfo from './single-template/TemplateInfo';
import TemplatesMenuEmptyState from './TemplatesMenuEmptyState';
import { TEMPLATES_MENU_DURATION } from './templates-menu-motion';
import { updateTemplateHebrewName } from 'api/templates';
import { getWhatsappTemplates } from 'store/slices/templates';
import { CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING } from 'layout/MainLayout/layout-zoom-constants';
import { useInView } from 'react-intersection-observer';
import type { MenuSingleTemplateProps } from 'types/templates/templates-menu';

/**
 * לא מרנדר MenuSingleTemplate לכל התבניות בבת אחת — מוריד long tasks ו־forced reflow בפתיחת מגירת בונה בוטים.
 * משתמש ב־ref נשלח ל־ListItem אחרי היידרציה.
 */
const TEMPLATES_MENU_EAGER_ROW_COUNT = 15;

const TemplatesMenuLazyRow = React.memo(function TemplatesMenuLazyRow(props: MenuSingleTemplateProps) {
  const selected = Boolean(props.selected);
  const listIndex = props.listIndex ?? 99999;
  const { ref: intersectionRef, inView } = useInView({
    rootMargin: '320px 240px 400px 240px',
    threshold: 0,
  });
  const [hydrated, setHydrated] = React.useState(
    () => selected || listIndex < TEMPLATES_MENU_EAGER_ROW_COUNT,
  );
  React.useEffect(() => {
    if (selected || inView) setHydrated(true);
  }, [selected, inView]);

  if (!hydrated) {
    return (
      <Box
        ref={intersectionRef as React.Ref<HTMLDivElement>}
        sx={{
          flexShrink: 0,
          minHeight: 88,
          borderRadius: 1,
          mb: props.compact ? 0.25 : 0.5,
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
        }}
        aria-hidden
      />
    );
  }
  return <MenuSingleTemplate {...props} listItemRef={intersectionRef} />;
});

/**
 * Templates menu. Can be used for sending templates or just for updating value
 * All props should be memoized
 */

const TemplatesMenu: React.FC<TemplatesMenuProps> = ({
  sendable = false,
  closable = false,
  onClose = () => {},
  chatUser,
  noUpload = false,
  onSendTemplate = () => {},
  onSelectedTemplateChange,
  initialSelectedTemplate,
  disablePadding,
  disableMediaSelection,
  onlyMediaTemplates,
  disableParams,
  initialMediaFile,
  isPriorityModal = false,
  compact = false,
  confirmApply = false,
  afterConfirmApply,
  scrollableListLayout = false,
  templateListScrollMode = 'outer',
  disableListRowEnterAnimation = false,
  lockExpandedCardLayout = false,
  twoStepSelection = false,
  onViewModeChange,
  embeddedInPanel = false,
  templateExtraVariables,
  templateExtraVariablesSectionLabel,
}) => {
  const theme = useTheme();
  const { currentLocale } = useLocalization();
  const templatesMenuLoc = currentLocale.templatesMenu;

  //* data & states
  const { whatsappTemplates, whatsappOriginalTemplates } = useSelector((state) => state.templates);
  const [filters, setFilters] = React.useState<TemplatesMenuFilters>({
    isActive: true,
    isHebrew: true,
    language: ['all'],
    searchQuery: '',
    selectedTags: [],
    sortBy: 'lastUsed',
    showMarketing: true,
    showService: true,
    showSecondaryTemplateName: false,
  });
  const [selectedTemplateId, setSelectedTemplateId] = React.useState<string | null>(null);
  const preferSelectModeRef = React.useRef(false);
  const [configureMode, setConfigureMode] = React.useState(
    () => Boolean(twoStepSelection && initialSelectedTemplate?.templateName),
  );

  const setViewMode = React.useCallback(
    (mode: 'select' | 'configure') => {
      setConfigureMode(mode === 'configure');
      onViewModeChange?.(mode);
    },
    [onViewModeChange],
  );

  /**
   * אינדוקס תבניות פעם אחת לכל שינוי store.
   * מפחית find() חוזר ונשנה בתוך filter/sort של הרשימה.
   */
  const templatesLookup = React.useMemo(() => {
    const byId = new Map<string, (typeof whatsappTemplates)[number]>();
    const byName = new Map<string, (typeof whatsappTemplates)[number]>();
    for (const t of whatsappTemplates) {
      if (t?.id != null) byId.set(String(t.id), t);
      if (t?.name) byName.set(String(t.name), t);
    }
    const resolve = (originalTemplate: (typeof whatsappOriginalTemplates)[number]) => {
      const byTemplateId =
        originalTemplate?.id != null ? byId.get(String(originalTemplate.id)) : undefined;
      if (byTemplateId) return byTemplateId;
      return originalTemplate?.name ? byName.get(String(originalTemplate.name)) : undefined;
    };
    return { resolve };
  }, [whatsappTemplates, whatsappOriginalTemplates]);

  /** ספירה לפי קטגוריית מטא — תואמת מסנני בסיס (פעילות, מדיה) בלי סינון קטגוריה */
  const categoryTemplateCounts = React.useMemo(() => {
    let marketing = 0;
    let service = 0;
    for (const t of whatsappOriginalTemplates) {
      // רק תבניות שמטא אישרה — תבנית בבדיקה (PENDING) או דחויה ממילא תיכשל בשליחה
      const isActiveCondition = t.status === 'APPROVED';
      if (!isActiveCondition) continue;
      if (onlyMediaTemplates) {
        const hasDoc = t.components?.some(
          (c) => c.type === 'HEADER' && c.format === 'DOCUMENT',
        );
        if (!hasDoc) continue;
      }
      const cat = String(t.category || '').toUpperCase();
      if (cat === 'MARKETING') marketing += 1;
      else if (cat === 'UTILITY') service += 1;
    }
    return { marketing, service };
  }, [whatsappOriginalTemplates, filters.isActive, onlyMediaTemplates]);

  const filteredTemplates = React.useMemo(() => {
    const selectedTagsSet =
      filters.selectedTags.length > 0 ? new Set(filters.selectedTags) : null;
    const searchQuery = filters?.searchQuery?.trim().toLowerCase() ?? '';

    const filteredOriginalTemplates = whatsappOriginalTemplates.filter((template) => {
      const hebrewTemplate = templatesLookup.resolve(template);
      // רק תבניות שמטא אישרה — תבנית בבדיקה (PENDING) או דחויה ממילא תיכשל בשליחה
      const isActiveCondition = template.status === 'APPROVED';
      // const languageCondition =
      //   template.language === (filters.language || languages[0]);
      const languageCondition = filters.language.includes('all')
        ? true
        : filters.language?.length
          ? filters.language.includes(template.language)
          : true;
      const searchCondition =
        searchQuery !== ''
          ? (() => {
              const englishName = template.name.toLowerCase();
              const hebrewName = ((hebrewTemplate?.hebrewName as any)?.hebrewTemaplateName || '').toLowerCase();
              const templateLabels = ((hebrewTemplate?.hebrewName as any)?.labels || []) as string[];
              
              return (
                englishName.includes(searchQuery) ||
                hebrewName.includes(searchQuery) ||
                templateLabels.some((label: string) => label.toLowerCase().includes(searchQuery))
              );
            })()
          : true;

      const onlyMediaCondition = onlyMediaTemplates
        ? template.components.some(
            (component) => component.type === 'HEADER' && component.format === 'DOCUMENT',
          )
        : true;

      // Tags filter condition
      const tagsCondition = !selectedTagsSet
        ? true
        : (() => {
            const templateLabels = ((hebrewTemplate?.hebrewName as any)?.labels || []) as string[];
            return templateLabels.some((tag: string) => selectedTagsSet.has(tag));
          })();

      const cat = String(template.category || '').toUpperCase();
      const categoryCondition = (() => {
        if (cat === 'MARKETING') return filters.showMarketing;
        if (cat === 'UTILITY') return filters.showService;
        return filters.showMarketing || filters.showService;
      })();

      return (
        isActiveCondition &&
        languageCondition &&
        searchCondition &&
        onlyMediaCondition &&
        tagsCondition &&
        categoryCondition
      );
    });

    const finalList = filteredOriginalTemplates.map((originalTemplate) => {
      const template = templatesLookup.resolve(originalTemplate);
      const templateObject = {
        originalTemplate,
        template,
      };
      return templateObject;
    });

    // Sort templates
    if (filters.sortBy === 'name') {
      finalList.sort((a, b) => {
        const aName = filters.isHebrew 
          ? (a.template?.hebrewName?.hebrewTemaplateName || a.originalTemplate.name)
          : a.originalTemplate.name;
        const bName = filters.isHebrew 
          ? (b.template?.hebrewName?.hebrewTemaplateName || b.originalTemplate.name)
          : b.originalTemplate.name;
        return aName.localeCompare(bName, 'he');
      });
    } else if (filters.sortBy === 'lastUsed') {
      // Sort by last used date (most recent first)
      finalList.sort((a, b) => {
        const aLastUsed = a.originalTemplate.last_used;
        const bLastUsed = b.originalTemplate.last_used;
        
        // Templates without last_used should go to the end
        if (!aLastUsed && !bLastUsed) return 0;
        if (!aLastUsed) return 1;
        if (!bLastUsed) return -1;
        
        // Sort by date (most recent first)
        return new Date(bLastUsed).getTime() - new Date(aLastUsed).getTime();
      });
    }

    return finalList;
  }, [
    filters.isActive,
    filters.language,
    filters.searchQuery,
    filters.selectedTags,
    filters.sortBy,
    filters.isHebrew,
    filters.showMarketing,
    filters.showService,
    onlyMediaTemplates,
    whatsappOriginalTemplates,
    templatesLookup,
  ]);

  const selectedTemplatePair = React.useMemo(() => {
    if (selectedTemplateId == null) return null;
    return (
      filteredTemplates.find(
        (item) => String(item.originalTemplate.id) === selectedTemplateId,
      ) ?? null
    );
  }, [filteredTemplates, selectedTemplateId]);

  const selectedTemplateDisplayName = React.useMemo(() => {
    if (!selectedTemplatePair) return '';
    const { originalTemplate, template } = selectedTemplatePair;
    if (filters.isHebrew) {
      return (
        (template?.hebrewName as { hebrewTemaplateName?: string } | undefined)
          ?.hebrewTemaplateName || originalTemplate.name
      );
    }
    return originalTemplate.name;
  }, [filters.isHebrew, selectedTemplatePair]);

  const showTemplateSelectionStep = twoStepSelection && !configureMode;
  const showTemplateConfigureStep = twoStepSelection && configureMode && selectedTemplatePair;

  const showCategoryVacuumEmpty = React.useMemo(() => {
    if (filteredTemplates.length > 0) return false;
    const narrowForVacuum =
      !filters.searchQuery?.trim() &&
      filters.selectedTags.length === 0 &&
      filters.language.includes('all');
    if (!narrowForVacuum) return false;
    if (filters.showMarketing && !filters.showService && categoryTemplateCounts.marketing === 0) {
      return true;
    }
    if (!filters.showMarketing && filters.showService && categoryTemplateCounts.service === 0) {
      return true;
    }
    return false;
  }, [
    filteredTemplates.length,
    filters.searchQuery,
    filters.selectedTags,
    filters.language,
    filters.showMarketing,
    filters.showService,
    categoryTemplateCounts.marketing,
    categoryTemplateCounts.service,
  ]);

  const handleSelectedId = React.useCallback((id: string | number) => {
    setSelectedTemplateId(id != null ? String(id) : null);
  }, []);

  const handlePickTemplate = React.useCallback(
    (id: string | number) => {
      //* בחירה מפורשת של המשתמש גוברת על השחזור האוטומטי. קודם הוגדר כאן false,
      //* ואז אפקט השחזור רץ מיד, מצא את התבנית השמורה הישנה ודרס את הבחירה —
      //* כך שלא היה אפשר להחליף תבנית כלל. ההגנה נפתחת שוב מעצמה ברגע
      //* שהבחירה מתפרסמת החוצה (האפקט שמאזין ל-initialSelectedTemplate.templateName).
      preferSelectModeRef.current = true;
      handleSelectedId(id);
      if (twoStepSelection) {
        setViewMode('configure');
      }
    },
    [handleSelectedId, setViewMode, twoStepSelection],
  );

  const handleBackToTemplateSelection = React.useCallback(() => {
    preferSelectModeRef.current = true;
    setViewMode('select');
  }, [setViewMode]);
  const resetTemplate = React.useCallback(() => {
    setSelectedTemplateId(null);
    if (onSelectedTemplateChange)
      onSelectedTemplateChange({
        sendObject: null,
        frontTemplateObject: null,
        components: null,
        valid: false,
      });
  }, [onSelectedTemplateChange]);

  const saveHebrewDisplayNameForTemplate = React.useCallback(
    async (templateEnglishName: string, hebrewDisplayName: string) => {
      try {
        const data = await updateTemplateHebrewName(templateEnglishName, hebrewDisplayName);
        if (data?.result) {
          getWhatsappTemplates();
          return true;
        }
      } catch {
        // שגיאת רשת / שרת — מוצג Snackbar במקומות הקריאה ל־API
      }
      return false;
    },
    [],
  );

  //* set default template on init
  React.useEffect(() => {
    if (!initialSelectedTemplate || preferSelectModeRef.current) return;

    const template = whatsappOriginalTemplates.find(
      (template) => template.name === initialSelectedTemplate.templateName,
    );
    if (template) {
      setSelectedTemplateId(template.id != null ? String(template.id) : null);
      if (twoStepSelection) {
        setViewMode('configure');
      }
    }
  }, [initialSelectedTemplate, twoStepSelection, setViewMode, whatsappOriginalTemplates]);

  React.useEffect(() => {
    preferSelectModeRef.current = false;
  }, [initialSelectedTemplate?.templateName]);

  React.useEffect(() => {
    if (!twoStepSelection) return;
    onViewModeChange?.(configureMode ? 'configure' : 'select');
  }, [configureMode, onViewModeChange, twoStepSelection]);

  React.useEffect(() => {
    if (twoStepSelection && configureMode && !selectedTemplatePair) {
      setViewMode('select');
    }
  }, [configureMode, selectedTemplatePair, setViewMode, twoStepSelection]);

  /** בפאנל צד (צ׳אט) — מיקוד לשדה החיפוש בפתיחה כדי שלא יידרש Tab דרך שאר האלמנטים */
  const didFocusSearchOnSidebarOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (!disablePadding || didFocusSearchOnSidebarOpenRef.current) return;
    didFocusSearchOnSidebarOpenRef.current = true;
    const raf = window.requestAnimationFrame(() => {
      document.getElementById('templates-menu-search-input')?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [disablePadding]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="column" spacing={0} sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden' }}>
        {/* Header — רק בשלב בחירת תבנית */}
        {!showTemplateConfigureStep && (
          <TemplatesMenuHeader
            closable={closable}
            onClose={onClose}
            chatUserName={chatUser?.FullName}
            filters={filters}
            setFilters={setFilters}
            disablePadding={disablePadding}
            isPriorityModal={isPriorityModal}
            categoryTemplateCounts={categoryTemplateCounts}
            compact={compact}
            embeddedInPanel={embeddedInPanel}
          />
        )}
        {showTemplateConfigureStep && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              flexShrink: 0,
              px: embeddedInPanel ? 1 : disablePadding ? 0 : 2,
              py: 1.25,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Button
              size="small"
              color="inherit"
              startIcon={
                <ArrowBackIosOutlinedIcon
                  sx={{ fontSize: 16, transform: (t) => (t.direction === 'rtl' ? 'rotate(180deg)' : undefined) }}
                />
              }
              onClick={handleBackToTemplateSelection}
              sx={{ flexShrink: 0 }}
            >
              {templatesMenuLoc.twoStep?.changeTemplate ?? 'שנה תבנית'}
            </Button>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              noWrap
              sx={{ flex: 1, minWidth: 0, textAlign: 'start' }}
            >
              {selectedTemplateDisplayName}
            </Typography>
          </Stack>
        )}
        {/* Templates list — ריפוד אופקי רק כאן (לא על מעטפת כל הפאנל / לא על כותרת RightAppSidebar) */}
        <Box
          style={
            disablePadding && !embeddedInPanel
              ? {
                  paddingLeft: theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                  paddingRight: theme.spacing(CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING),
                  boxSizing: 'border-box',
                }
              : undefined
          }
          sx={{
            flex: scrollableListLayout ? '1 1 0' : '1 1 auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {showTemplateConfigureStep ? (
            <Fade in timeout={TEMPLATES_MENU_DURATION.fadeSlow}>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  px: embeddedInPanel ? 1 : disablePadding ? 0 : 1,
                  py: 1,
                }}
              >
                <MenuSingleTemplate
                  key={selectedTemplatePair.originalTemplate.id}
                  template={selectedTemplatePair.template}
                  originalTemplate={selectedTemplatePair.originalTemplate}
                  selected
                  onSelect={handleSelectedId}
                  isHebrew={filters.isHebrew}
                  resetTemplate={resetTemplate}
                  onSaveHebrewDisplayName={(hebrewDisplayName: string) =>
                    saveHebrewDisplayNameForTemplate(
                      selectedTemplatePair.originalTemplate.name,
                      hebrewDisplayName,
                    )
                  }
                  noUpload={noUpload}
                  chatUser={chatUser}
                  sendable={sendable}
                  onSendTemplate={onSendTemplate}
                  onClose={onClose}
                  onSelectedTemplateChange={onSelectedTemplateChange}
                  disablePadding={disablePadding}
                  initialSelectedTemplate={initialSelectedTemplate}
                  disableMediaSelection={disableMediaSelection}
                  disableParams={disableParams}
                  initialMediaFile={initialMediaFile}
                  showSecondaryTemplateName={filters.showSecondaryTemplateName}
                  showMarketing={filters.showMarketing}
                  showService={filters.showService}
                  compact={compact}
                  confirmApply={confirmApply}
                  afterConfirmApply={afterConfirmApply}
                  lockExpandedCardLayout={lockExpandedCardLayout ?? true}
                  focusedConfigurationMode
                  embeddedWizardPreview
                  templatePreviewWhatsappStyle
                  disableListRowEnterAnimation
                  templateExtraVariables={templateExtraVariables}
                  templateExtraVariablesSectionLabel={templateExtraVariablesSectionLabel}
                />
              </Box>
            </Fade>
          ) : filteredTemplates.length > 0 ? (
            showTemplateSelectionStep ? (
              <List
                component="div"
                role="list"
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '100%',
                  py: 0,
                  px: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                  scrollBehavior: 'smooth',
                  '@media (prefers-reduced-motion: reduce)': {
                    scrollBehavior: 'auto',
                  },
                  overscrollBehaviorY: 'contain',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {filteredTemplates.map((template) => {
                  const templateHeader = template.originalTemplate.components?.find(
                    (comp) => comp.type === 'HEADER',
                  );
                  const templateBody = template.originalTemplate.components?.find(
                    (comp) => comp.type === 'BODY',
                  );
                  const isRowSelected =
                    selectedTemplateId != null &&
                    String(template.originalTemplate.id) === selectedTemplateId;
                  return (
                    <ListItemButton
                      key={template.originalTemplate.id}
                      selected={isRowSelected}
                      onClick={() => handlePickTemplate(template.originalTemplate.id!)}
                      sx={{
                        alignItems: 'flex-start',
                        py: 1.25,
                        px: embeddedInPanel ? 1 : disablePadding ? 1 : 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                        borderRadius: 0,
                        bgcolor: 'background.paper',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <Stack spacing={0.75} width="100%">
                        <TemplateInfo
                          originalTemplate={template.originalTemplate}
                          template={template.template}
                          selected={isRowSelected}
                          isHebrew={filters.isHebrew}
                          hebrewName={template.template?.hebrewName ?? null}
                          templateHeader={templateHeader}
                          resetTemplate={resetTemplate}
                          onSaveHebrewDisplayName={(hebrewDisplayName: string) =>
                            saveHebrewDisplayNameForTemplate(
                              template.originalTemplate.name,
                              hebrewDisplayName,
                            )
                          }
                          showSecondaryTemplateName={filters.showSecondaryTemplateName}
                          showMarketing={filters.showMarketing}
                          showService={filters.showService}
                          compact
                          hideAccordionControl
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ paddingInlineStart: 0.5, textAlign: 'start' }}
                        >
                          {templateBody?.text && templateBody.text.length > 48
                            ? `${templateBody.text.slice(0, 48)}…`
                            : templateBody?.text}
                        </Typography>
                      </Stack>
                    </ListItemButton>
                  );
                })}
              </List>
            ) : (
            <List
              component="div"
              role="list"
              sx={{
                flex: scrollableListLayout && templateListScrollMode === 'inner' ? '1 1 0' : 1,
                minHeight: scrollableListLayout && templateListScrollMode === 'inner' ? 0 : undefined,
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                width: '100%',
                py: 0,
                px: 0,
                ...(confirmApply && { paddingBottom: theme.spacing(2) }),
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                '& .MuiListItem-root': {
                  marginBottom: '0 !important',
                },
                scrollBehavior: 'smooth',
                '@media (prefers-reduced-motion: reduce)': {
                  scrollBehavior: 'auto',
                },
                overscrollBehaviorY: 'contain',
                WebkitOverflowScrolling: 'touch',
                ...(isPriorityModal && {
                  maxWidth: '80%',
                  margin: '0 auto',
                }),
              } as SxProps}
            >
              {filteredTemplates.map((template, index) => (
                <TemplatesMenuLazyRow
                  key={template.originalTemplate.id}
                  template={template.template}
                  originalTemplate={template.originalTemplate}
                  onSelect={handleSelectedId}
                  selected={
                    selectedTemplateId != null &&
                    String(template.originalTemplate.id) === selectedTemplateId
                  }
                  isHebrew={filters.isHebrew}
                  resetTemplate={resetTemplate}
                  listIndex={index}
                  onSaveHebrewDisplayName={(hebrewDisplayName: string) =>
                    saveHebrewDisplayNameForTemplate(template.originalTemplate.name, hebrewDisplayName)
                  }
                  noUpload={noUpload}
                  chatUser={chatUser}
                  sendable={sendable}
                  onSendTemplate={onSendTemplate}
                  onClose={onClose}
                  onSelectedTemplateChange={onSelectedTemplateChange}
                  disablePadding={disablePadding}
                  initialSelectedTemplate={initialSelectedTemplate}
                  disableMediaSelection={disableMediaSelection}
                  disableParams={disableParams}
                  initialMediaFile={initialMediaFile}
                  showSecondaryTemplateName={filters.showSecondaryTemplateName}
                  showMarketing={filters.showMarketing}
                  showService={filters.showService}
                  compact={compact}
                  confirmApply={confirmApply}
                  afterConfirmApply={afterConfirmApply}
                  disableListRowEnterAnimation={disableListRowEnterAnimation}
                  lockExpandedCardLayout={lockExpandedCardLayout}
                  templateExtraVariables={templateExtraVariables}
                  templateExtraVariablesSectionLabel={templateExtraVariablesSectionLabel}
                />
              ))}
            </List>
            )
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 0,
                overflowY: 'overlay',
                overflowX: 'hidden',
                width: '100%',
                ...(isPriorityModal && {
                  maxWidth: '80%',
                  margin: '0 auto',
                }),
              }}
            >
              <Fade in timeout={TEMPLATES_MENU_DURATION.fadeSlow} unmountOnExit>
                <Box sx={{ width: '100%' }}>
                  <TemplatesMenuEmptyState
                    variant={showCategoryVacuumEmpty ? 'categoryVacuum' : 'generic'}
                    categoryDisplayName={
                      filters.showMarketing && !filters.showService
                        ? templatesMenuLoc.filters.categoryMarketing
                        : templatesMenuLoc.filters.categoryService
                    }
                    genericMessage={templatesMenuLoc.filters.emptyState.generic}
                    titleCategory={templatesMenuLoc.filters.emptyState.categoryTitle}
                    descriptionCategory={templatesMenuLoc.filters.emptyState.categoryDescription}
                    ctaCategory={templatesMenuLoc.filters.emptyState.categoryCta}
                  />
                </Box>
              </Fade>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default React.memo(TemplatesMenu);
